import CheckPermission from '../../../hooks/CheckPermission';
import React, { useState } from 'react'
import {
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaFlask,
  FaXRay,
  FaTrash
} from 'react-icons/fa'
import CustomButton from '../../../shared/CustomButton'
import toast from 'react-hot-toast'

const LabsImagingTab = ({ patient }) => {
  const { hasPermission } = CheckPermission();
  // Sample data
  const [doctors] = useState([
    { id: 1, name: 'Dr. John Smith' },
    { id: 2, name: 'Dr. Sarah Johnson' },
    { id: 3, name: 'Dr. Michael Brown' },
    { id: 4, name: 'Dr. Emily Davis' }
  ])

  const [referrers] = useState([
    { id: 1, name: 'Internal Medicine' },
    { id: 2, name: 'Cardiology' },
    { id: 3, name: 'Neurology' },
    { id: 4, name: 'Orthopedics' }
  ])

  const [patients] = useState([
    { id: 1, name: patient?.name || 'John Doe' },
    { id: 2, name: 'Jane Smith' },
    { id: 3, name: 'Bob Johnson' }
  ])

  const [examiners] = useState([
    { id: 1, name: 'Dr. Robert Wilson' },
    { id: 2, name: 'Dr. Lisa Anderson' },
    { id: 3, name: 'Dr. James Taylor' }
  ])

  // Test panels
  const testPanels = {
    'Acute Hepatitis Panel': [
      'Hepatitis A, IgM antibody',
      'Hepatitis B core, IgM antibody',
      'Hepatitis B surface antigen',
      'Hepatitis C antibody'
    ],
    'Lipid Panel': [
      'Hepatitis A, IgM antibody',
      'Hepatitis B core, IgM antibody',
      'Hepatitis B surface antigen',
      'Hepatitis C antibody'
    ],
    'Electrolyte Panel': [
      'Hepatitis A, IgM antibody',
      'Hepatitis B core, IgM antibody',
      'Hepatitis B surface antigen',
      'Hepatitis C antibody'
    ],
    'Renal Function Panel': [
      'Hepatitis A, IgM antibody',
      'Hepatitis B core, IgM antibody',
      'Hepatitis B surface antigen',
      'Hepatitis C antibody'
    ]
  }

  // State for dialogs
  const [showLabDialog, setShowLabDialog] = useState(false)
  const [showScanDialog, setShowScanDialog] = useState(false)

  // State for lab requests
  const [labRequests, setLabRequests] = useState([
    // {
    //   id: 1,
    //   name: "Complete Blood Count",
    //   date: "2025-06-25",
    //   requestedBy: "Dr. John Smith",
    //   status: "Completed",
    //   reason: "Routine checkup",
    //   referrer: "Internal Medicine",
    //   priority: "Medium",
    //   location: "Lab A",
    // },
    // {
    //   id: 2,
    //   name: "Lipid Panel",
    //   date: "2025-06-26",
    //   requestedBy: "Dr. Sarah Johnson",
    //   status: "In Progress",
    //   reason: "Cholesterol monitoring",
    //   referrer: "Cardiology",
    //   priority: "High",
    //   location: "Lab B",
    // },
  ])

  // State for scan requests
  const [scanRequests, setScanRequests] = useState([
    // {
    //   id: 1,
    //   name: 'X-Ray of Chest',
    //   date: '2025-06-24',
    //   requestedBy: 'Dr. Michael Brown',
    //   status: 'Completed',
    //   procedure: 'X-Ray of Chest',
    //   modality: 'X-Ray',
    //   price: '150',
    //   reason: 'Chest pain investigation'
    // },
    // {
    //   id: 2,
    //   name: 'Ultrasound of Thyroid',
    //   date: '2025-06-27',
    //   requestedBy: 'Dr. Emily Davis',
    //   status: 'Pending',
    //   procedure: 'Ultrasound of Thyroid',
    //   modality: 'Ultrasound',
    //   price: '200',
    //   reason: 'Thyroid nodule assessment'
    // }
  ])

  // Form states
  const [labForm, setLabForm] = useState({
    date: new Date().toISOString().split('T')[0],
    name: '',
    reason: '',
    requestedBy: '',
    referrer: '',
    patientId: 1,
    status: 'Pending',
    priority: 'Medium',
    location: '',
    file: null,
    selectedPanels: []
  })

  const [scanForm, setScanForm] = useState({
    date: new Date().toISOString().split('T')[0],
    procedure: '',
    name: '',
    modality: '',
    price: '',
    reason: '',
    requestedBy: '',
    patientId: 1,
    status: 'Pending',
    examinationDate: '',
    examinedBy: '',
    examinationDevice: '',
    examinationComment: '',
    validatedBy: '',
    validationDate: '',
    dateCompleted: '',
    files: []
  })

  const procedures = [
    'X-Ray of Spine',
    'X-Ray of Hand',
    'X-Ray of Foot',
    'X-Ray of Chest',
    'X-Ray of Abdomen',
    'Ultrasound of Thyroid',
    'Ultrasound of Pregnancy',
    'Ultrasound of Pelvis',
    'Ultrasound of the Kidney'
  ]

  const modalities = ['CT Scan', 'MRI', 'X-Ray', 'Ultrasound', 'PET']

  const labStatuses = [
    'Pending',
    'Specimen Taken',
    'Specimen Received',
    'In Progress',
    'Sent to Machine',
    'Results Entered',
    'Checked',
    'Problem',
    'Approved',
    'Completed'
  ]

  const scanStatuses = [
    'Pending',
    'Examined',
    'Preliminary Report',
    'Final Report',
    'Completed'
  ]

  const priorities = ['High', 'Medium', 'Low']

  // Handle adding test panel
  const handleAddTestPanel = panelName => {
    if (labForm.selectedPanels.find(p => p.name === panelName)) {
      toast.error('Panel already added')
      return
    }

    const newPanel = {
      id: Date.now(),
      name: panelName,
      tests: [...testPanels[panelName]]
    }

    setLabForm({
      ...labForm,
      selectedPanels: [...labForm.selectedPanels, newPanel]
    })
  }

  // Handle removing test panel
  const handleRemoveTestPanel = panelId => {
    if (!hasPermission('labOrders', 'create')) return;
    setLabForm({
      ...labForm,
      selectedPanels: labForm.selectedPanels.filter(p => p.id !== panelId)
    })
  }

  // Handle removing individual test
  const handleRemoveTest = (panelId, testIndex) => {
    if (!hasPermission('labOrders', 'create')) return;
    setLabForm({
      ...labForm,
      selectedPanels: labForm.selectedPanels.map(panel =>
        panel.id === panelId
          ? {
              ...panel,
              tests: panel.tests.filter((_, index) => index !== testIndex)
            }
          : panel
      )
    })
  }

  // Handle procedure selection
  const handleProcedureChange = procedure => {
    setScanForm({
      ...scanForm,
      procedure: procedure,
      name: procedure
    })
  }

  // Handle lab form submission
  const handleLabSubmit = () => {
    if (!labForm.name || !labForm.requestedBy) {
      toast.error('Please fill in required fields')
      return
    }

    const newLabRequest = {
      id: Date.now(),
      ...labForm,
      requestedBy:
        doctors.find(d => d.id === parseInt(labForm.requestedBy))?.name || '',
      referrer:
        referrers.find(r => r.id === parseInt(labForm.referrer))?.name || ''
    }

    setLabRequests([...labRequests, newLabRequest])
    setLabForm({
      date: new Date().toISOString().split('T')[0],
      name: '',
      reason: '',
      requestedBy: '',
      referrer: '',
      patientId: 1,
      status: 'Pending',
      priority: 'Medium',
      location: '',
      file: null,
      selectedPanels: []
    })
    setShowLabDialog(false)
    toast.success('Lab request created successfully')
  }

  // Handle scan form submission
  const handleScanSubmit = () => {
    if (!scanForm.name || !scanForm.requestedBy) {
      toast.error('Please fill in required fields')
      return
    }

    const newScanRequest = {
      id: Date.now(),
      ...scanForm,
      requestedBy:
        doctors.find(d => d.id === parseInt(scanForm.requestedBy))?.name || '',
      examinedBy:
        examiners.find(e => e.id === parseInt(scanForm.examinedBy))?.name || ''
    }

    setScanRequests([...scanRequests, newScanRequest])
    setScanForm({
      date: new Date().toISOString().split('T')[0],
      procedure: '',
      name: '',
      modality: '',
      price: '',
      reason: '',
      requestedBy: '',
      patientId: 1,
      status: 'Pending',
      examinationDate: '',
      examinedBy: '',
      examinationDevice: '',
      examinationComment: '',
      validatedBy: '',
      validationDate: '',
      dateCompleted: '',
      files: []
    })
    setShowScanDialog(false)
    toast.success('Scan request created successfully')
  }

  // Get status color
  const getStatusColor = status => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800'
      case 'In Progress':
      case 'Examined':
        return 'bg-blue-100 text-blue-800'
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'Problem':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className='h-full flex flex-col'>
      {/* Header */}
      <div className='p-6 pb-4 flex-shrink-0'>
        <div className='flex items-center justify-between mb-6'>
          <h2 className='text-2xl font-bold'>Labs & Imaging</h2>
          <div className='flex space-x-3'>
            <CustomButton
              onClick={() => setShowLabDialog(true)}
              label='Request Lab'
              icon={<FaFlask size={14} />}
              buttonType='primary'
              className='!h-[40px]'
            />
            <CustomButton
              onClick={() => setShowScanDialog(true)}
              label='Request Scan'
              icon={<FaXRay size={14} />}
              buttonType='secondary'
              className='!h-[40px]'
            />
          </div>
        </div>
      </div>

      {/* Requests Display */}
      <div className='flex-1 px-6 pb-6 overflow-y-auto'>
        {/* Lab Requests */}
        <div className='mb-8'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
            <FaFlask className='mr-2 text-blue-600' />
            Lab Requests ({labRequests.length})
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {labRequests.map(request => (
              <div
                key={request.id}
                className='bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow'
              >
                <div className='flex items-start justify-between mb-3'>
                  <h4 className='font-semibold text-gray-900 text-sm'>
                    {request.name}
                  </h4>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      request.status
                    )}`}
                  >
                    {request.status}
                  </span>
                </div>
                <div className='space-y-2 text-sm text-gray-600'>
                  <div className='flex items-center'>
                    <FaCalendarAlt className='mr-2 text-gray-400' size={12} />
                    <span>{new Date(request.date).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <strong>Requested by:</strong> {request.requestedBy}
                  </div>
                  <div>
                    <strong>Priority:</strong> {request.priority}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Radiology Requests */}
        <div>
          <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
            <FaXRay className='mr-2 text-purple-600' />
            Radiology Requests ({scanRequests.length})
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {scanRequests.map(request => (
              <div
                key={request.id}
                className='bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow'
              >
                <div className='flex items-start justify-between mb-3'>
                  <h4 className='font-semibold text-gray-900 text-sm'>
                    {request.name}
                  </h4>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      request.status
                    )}`}
                  >
                    {request.status}
                  </span>
                </div>
                <div className='space-y-2 text-sm text-gray-600'>
                  <div className='flex items-center'>
                    <FaCalendarAlt className='mr-2 text-gray-400' size={12} />
                    <span>{new Date(request.date).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <strong>Requested by:</strong> {request.requestedBy}
                  </div>
                  <div>
                    <strong>Modality:</strong> {request.modality}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Request Lab Dialog */}
      {showLabDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-6'>
                <h3 className='text-xl font-semibold text-gray-900 flex items-center'>
                  <FaFlask className='mr-2 text-blue-600' />
                  Request Lab
                </h3>
                <button
                  onClick={() => setShowLabDialog(false)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className='grid grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Date *
                  </label>
                  <input
                    type='date'
                    value={labForm.date}
                    onChange={e =>
                      setLabForm({ ...labForm, date: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Name *
                  </label>
                  <input
                    type='text'
                    value={labForm.name}
                    onChange={e =>
                      setLabForm({ ...labForm, name: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Lab test name'
                  />
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Reason
                  </label>
                  <textarea
                    value={labForm.reason}
                    onChange={e =>
                      setLabForm({ ...labForm, reason: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    rows='3'
                    placeholder='Reason for test'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Requested By *
                  </label>
                  <select
                    value={labForm.requestedBy}
                    onChange={e =>
                      setLabForm({ ...labForm, requestedBy: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Referrer
                  </label>
                  <select
                    value={labForm.referrer}
                    onChange={e =>
                      setLabForm({ ...labForm, referrer: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Referrer</option>
                    {referrers.map(referrer => (
                      <option key={referrer.id} value={referrer.id}>
                        {referrer.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Patient
                  </label>
                  <select
                    value={labForm.patientId}
                    disabled
                    className='w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100'
                  >
                    {patients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Status
                  </label>
                  <select
                    value={labForm.status}
                    onChange={e =>
                      setLabForm({ ...labForm, status: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    {labStatuses.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Add Lab Tests
                  </label>
                  <select
                    onChange={e => {
                      if (e.target.value) {
                        handleAddTestPanel(e.target.value)
                        e.target.value = ''
                      }
                    }}
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Test Panel</option>
                    {Object.keys(testPanels).map(panel => (
                      <option key={panel} value={panel}>
                        {panel}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Selected Test Panels */}
                {labForm.selectedPanels.length > 0 && (
                  <div className='col-span-2'>
                    <h4 className='text-sm font-medium text-gray-700 mb-3'>
                      Selected Test Panels
                    </h4>
                    <div className='space-y-3'>
                      {labForm.selectedPanels.map(panel => (
                        <div
                          key={panel.id}
                          className='border border-gray-200 rounded-lg p-4'
                        >
                          <div className='flex items-center justify-between mb-3'>
                            <h5 className='font-medium text-gray-900'>
                              {panel.name}
                            </h5>
                            <button
                              onClick={() => handleRemoveTestPanel(panel.id)}
                              className='text-red-500 hover:text-red-700'
                            >
                              <FaTimes size={16} />
                            </button>
                          </div>
                          <div className='space-y-2'>
                            {panel.tests.map((test, index) => (
                              <div
                                key={index}
                                className='flex items-center justify-between text-sm text-gray-600'
                              >
                                <span>{test}</span>
                                <button
                                  onClick={() =>
                                    handleRemoveTest(panel.id, index)
                                  }
                                  className='text-red-500 hover:text-red-700 ml-2'
                                >
                                  <FaTrash size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Priority
                  </label>
                  <select
                    value={labForm.priority}
                    onChange={e =>
                      setLabForm({ ...labForm, priority: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Location
                  </label>
                  <input
                    type='text'
                    value={labForm.location}
                    onChange={e =>
                      setLabForm({ ...labForm, location: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Lab location'
                  />
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    File
                  </label>
                  <div className='border border-gray-300 rounded-md px-3 py-2'>
                    <input
                      type='file'
                      onChange={e =>
                        setLabForm({ ...labForm, file: e.target.files[0] })
                      }
                      className='w-full'
                      accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                    />
                  </div>
                </div>
              </div>

              <div className='flex items-center justify-end space-x-3 mt-6 pt-6 border-t'>
                <CustomButton
                  onClick={() => setShowLabDialog(false)}
                  label='Cancel'
                  buttonType='reset'
                  className='!h-[40px]'
                />
                <CustomButton
                  onClick={handleLabSubmit}
                  label='Create Request'
                  icon={<FaPlus size={14} />}
                  buttonType='primary'
                  className='!h-[40px]'
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Scan Dialog */}
      {showScanDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-6'>
                <h3 className='text-xl font-semibold text-gray-900 flex items-center'>
                  <FaXRay className='mr-2 text-purple-600' />
                  Request Scan
                </h3>
                <button
                  onClick={() => setShowScanDialog(false)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className='grid grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Date *
                  </label>
                  <input
                    type='date'
                    value={scanForm.date}
                    onChange={e =>
                      setScanForm({ ...scanForm, date: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Procedure
                  </label>
                  <select
                    value={scanForm.procedure}
                    onChange={e => handleProcedureChange(e.target.value)}
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Procedure</option>
                    {procedures.map(procedure => (
                      <option key={procedure} value={procedure}>
                        {procedure}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Name *
                  </label>
                  <input
                    type='text'
                    value={scanForm.name}
                    onChange={e =>
                      setScanForm({ ...scanForm, name: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Scan name'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Modality
                  </label>
                  <select
                    value={scanForm.modality}
                    onChange={e =>
                      setScanForm({ ...scanForm, modality: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Modality</option>
                    {modalities.map(modality => (
                      <option key={modality} value={modality}>
                        {modality}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Price
                  </label>
                  <input
                    type='text'
                    value={scanForm.price}
                    onChange={e =>
                      setScanForm({ ...scanForm, price: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Price'
                  />
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Reason
                  </label>
                  <input
                    type='text'
                    value={scanForm.reason}
                    onChange={e =>
                      setScanForm({ ...scanForm, reason: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Reason for scan'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Requested By *
                  </label>
                  <select
                    value={scanForm.requestedBy}
                    onChange={e =>
                      setScanForm({ ...scanForm, requestedBy: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Patient
                  </label>
                  <select
                    value={scanForm.patientId}
                    disabled
                    className='w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100'
                  >
                    {patients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Status
                  </label>
                  <select
                    value={scanForm.status}
                    onChange={e =>
                      setScanForm({ ...scanForm, status: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    {scanStatuses.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Examination Date
                  </label>
                  <input
                    type='date'
                    value={scanForm.examinationDate}
                    onChange={e =>
                      setScanForm({
                        ...scanForm,
                        examinationDate: e.target.value
                      })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Examined By
                  </label>
                  <select
                    value={scanForm.examinedBy}
                    onChange={e =>
                      setScanForm({ ...scanForm, examinedBy: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value=''>Select Examiner</option>
                    {examiners.map(examiner => (
                      <option key={examiner.id} value={examiner.id}>
                        {examiner.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Examination Device
                  </label>
                  <input
                    type='text'
                    value={scanForm.examinationDevice}
                    onChange={e =>
                      setScanForm({
                        ...scanForm,
                        examinationDevice: e.target.value
                      })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Device used'
                  />
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Examination Comment
                  </label>
                  <textarea
                    value={scanForm.examinationComment}
                    onChange={e =>
                      setScanForm({
                        ...scanForm,
                        examinationComment: e.target.value
                      })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    rows='3'
                    placeholder='Examination comments'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Validated By
                  </label>
                  <input
                    type='text'
                    value={scanForm.validatedBy}
                    onChange={e =>
                      setScanForm({ ...scanForm, validatedBy: e.target.value })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Validator name'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Validation Date
                  </label>
                  <input
                    type='date'
                    value={scanForm.validationDate}
                    onChange={e =>
                      setScanForm({
                        ...scanForm,
                        validationDate: e.target.value
                      })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Date Completed
                  </label>
                  <input
                    type='date'
                    value={scanForm.dateCompleted}
                    onChange={e =>
                      setScanForm({
                        ...scanForm,
                        dateCompleted: e.target.value
                      })
                    }
                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                <div className='col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Files
                  </label>
                  <div className='border border-gray-300 rounded-md px-3 py-2'>
                    <input
                      type='file'
                      multiple
                      onChange={e =>
                        setScanForm({
                          ...scanForm,
                          files: Array.from(e.target.files)
                        })
                      }
                      className='w-full'
                      accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.dcm'
                    />
                  </div>
                </div>
              </div>

              <div className='flex items-center justify-end space-x-3 mt-6 pt-6 border-t'>
                <CustomButton
                  onClick={() => setShowScanDialog(false)}
                  label='Cancel'
                  buttonType='reset'
                  className='!h-[40px]'
                />
                <CustomButton
                  onClick={handleScanSubmit}
                  label='Create Request'
                  icon={<FaPlus size={14} />}
                  buttonType='primary'
                  className='!h-[40px]'
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LabsImagingTab
